

import { createRouter, createWebHistory } from "vue-router";
import HomeView from "../views/HomeView.vue";
import AboutView from "../views/AboutView.vue";
import ContactsView from "../views/ContactsView.vue";
import ServicesView from "../views/ServicesView.vue";
import ServiceDetailView from "../views/ServiceDetailView.vue";
import VacanciesView from "../views/VacanciesView.vue";
import NotFoundView from "../views/NotFoundView.vue";
import servicesData from "@/data/services.json";
import i18n from "@/plugins/i18n";

const locales = ["en", "ru", "ro"];

// Функция обновления мета-данных
const updateMetaInfo = (route) => {
  document.querySelectorAll('meta[name], meta[property]').forEach(tag => tag.remove());

  let pageTitle = "Default Title";
  let metaTags = [];

  if (route.name === "serviceDetails") {
    const locale = route.params.locale || "en";
    const service = servicesData.find(service => service.url[locale] === route.params.target);

    if (service && service.meta[locale]) {
      const meta = service.meta[locale];
      pageTitle = meta.title;
      metaTags = [
        { name: "description", content: meta.description },
        { name: "keywords", content: meta.keywords },
        { property: "og:title", content: meta.ogTitle } // <-- Оставляем только здесь
      ];
    }
  } else {
    pageTitle = i18n.global.te(route.meta.title) ? i18n.global.t(route.meta.title) : "Default Title";
    metaTags = (route.meta.metaTags || [])
      .map(tag => ({
        name: tag.name,
        property: tag.property,
        content: i18n.global.te(tag.content) ? i18n.global.t(tag.content) : null
      }))
      .filter(tag => tag.content);
  }

  if (route.name === "NotFound") {
    pageTitle = "Страница не найдена";
    metaTags = [{ name: "robots", content: "noindex, nofollow" }]; // Запрещаем индексировать 404
  }

  document.title = pageTitle;

  metaTags.forEach(tag => { // <-- Убираем дополнительный `og:title`
    const meta = document.createElement("meta");
    if (tag.name) meta.setAttribute("name", tag.name);
    if (tag.property) meta.setAttribute("property", tag.property);
    meta.setAttribute("content", tag.content);
    document.head.appendChild(meta);
  });
};

// Функция проверки существования услуги
const checkServiceExists = (to) => {
  const locale = to.params.locale || "en";
  return servicesData.some(service => service.url[locale] === to.params.target);
};

// Функция создания локализованных маршрутов
const createLocalizedRoute = (path, name, component) => ({
  path: `/:locale/${path}`,
  name,
  component,
  meta: {
    title: `meta.${name}.title`,
    metaTags: [
      { name: "description", content: `meta.${name}.description` },
      { name: "keywords", content: `meta.${name}.keywords` },
      { property: "og:title", content: `meta.${name}.ogTitle` }
    ]
  }
});

// Функция генерации `alternate href`
const generateAlternateHrefs = (to) => {
  const locale = to.params.locale || "en";
  const currentUrl = window.location.origin;

  return locales.map(lang => {
    let localizedPath = "";

    if (to.name === "serviceDetails") {
      const service = servicesData.find(service => service.url[locale] === to.params.target);
      if (service) {
        localizedPath = `/${service.category[lang]}/${service.url[lang]}`;
      }
    } else {
      if (i18n.global.te(`url.${to.name}`)) {
        localizedPath = `/${i18n.global.t(`url.${to.name}`, lang)}`;
      } else {
        localizedPath = to.path.replace(/^\/[^/]+/, "");
      }
    }

    return {
      rel: "alternate",
      hreflang: lang,
      href: `${currentUrl}/${lang}${localizedPath}`,
    };
  });
};

// Функция обновления <link rel="alternate">
const updateAlternateLinks = (to) => {
  document.querySelectorAll('link[rel="alternate"]').forEach(el => el.remove());

  const alternateLinks = generateAlternateHrefs(to);

  alternateLinks.forEach(linkData => {
    const link = document.createElement("link");
    link.rel = linkData.rel;
    link.hreflang = linkData.hreflang;
    link.href = linkData.href;
    document.head.appendChild(link);
  });
};

// Определяем маршруты
const routes = [
  { path: "/", redirect: `/${localStorage.getItem("locale") || "en"}` },
  {
    path: "/:locale",
    children: [
      {
        path: "",
        name: "home",
        component: HomeView,
        meta: {
          title: "meta.home.title",
          metaTags: [
            { name: "description", content: "meta.home.description" },
            { name: "keywords", content: "meta.home.keywords" },
            { property: "og:title", content: "meta.home.ogTitle" }
          ]
        }
      },
      createLocalizedRoute("contacts", "contacts", ContactsView),
      createLocalizedRoute("about", "about", AboutView),
      createLocalizedRoute("services", "services", ServicesView),
      createLocalizedRoute("vacancies", "vacancies", VacanciesView),
      {
        path: ":category/:target",
        name: "serviceDetails",
        component: ServiceDetailView,
        beforeEnter: (to, from, next) => {
          if (checkServiceExists(to)) {
            updateMetaInfo(to); // Загружаем мета-данные перед рендерингом страницы
            next();
          } else {
            next({ name: "services" });
          }
        }
      }
    ]
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: NotFoundView
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

// Обновляем `alternate href` и мета-данные после смены маршрута
router.afterEach((to) => {
  if (typeof document !== "undefined") {
    updateMetaInfo(to);
    updateAlternateLinks(to);
  }
});

export default router;
